import { useEffect, useMemo, useState } from 'react';
import {
  dataTableHeadClass,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import {
  Callout,
  Card,
  LoadingSpinner,
  NumberInput,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { YearlyMonthlyReport } from '../../types/reports';
import { Office } from '../../types/office';
import { formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportYearlyMonthly } from './exportReports';

const SERIES_COLORS = {
  invoice: 'rgb(var(--bf-brand))',
  cash: 'rgb(var(--bf-accent))',
  payments: 'rgb(180 83 9)',
  chargesTotal: 'rgb(var(--bf-brand))',
  paymentsTotal: 'rgb(180 83 9)',
} as const;

export function YearlyMonthlyPage() {
  const labels = t().reports;
  const months = t().common.months;
  const [year, setYear] = useState(new Date().getFullYear());
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeId, setOfficeId] = useState('');
  const [report, setReport] = useState<YearlyMonthlyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.getOffices(false).then(setOffices);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getYearlyMonthlyReport(
          year,
          officeId || undefined,
        );
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [year, officeId]);

  function handleExport(format: 'excel' | 'pdf') {
    if (!report) return;
    exportYearlyMonthly({
      report,
      officeName:
        offices.find((office) => String(office.id) === officeId)?.name ??
        t().customers.filterAll,
      format,
    });
  }

  const chartSeries = useMemo(() => {
    if (!report) return null;
    return {
      grouped: [
        {
          key: 'invoice',
          label: labels.seriesInvoiceCharges,
          color: SERIES_COLORS.invoice,
          values: report.months.map((m) => Number(m.charges.invoice)),
        },
        {
          key: 'cash',
          label: labels.seriesCashCharges,
          color: SERIES_COLORS.cash,
          values: report.months.map((m) => Number(m.charges.cash)),
        },
        {
          key: 'payments',
          label: labels.seriesPayments,
          color: SERIES_COLORS.payments,
          values: report.months.map((m) => Number(m.payments.total)),
        },
      ],
      totals: [
        {
          key: 'charges',
          label: labels.seriesChargesTotal,
          color: SERIES_COLORS.chargesTotal,
          values: report.months.map((m) => Number(m.charges.total)),
        },
        {
          key: 'payments',
          label: labels.seriesPaymentsTotal,
          color: SERIES_COLORS.paymentsTotal,
          values: report.months.map((m) => Number(m.payments.total)),
        },
      ],
    };
  }, [report, labels]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report}
            onExcel={() => handleExport('excel')}
            onPdf={() => handleExport('pdf')}
          />
        }
      >
        <FilterField label={labels.year}>
          <NumberInput
            value={year}
            allowDecimals={false}
            showClearButton={false}
            size="sm"
            onChange={(e) => {
              if (e.target.value != null) setYear(e.target.value);
            }}
          />
        </FilterField>
        <FilterField label={t().nav.office}>
          <Select
            value={officeId}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: t().customers.filterAll },
              ...offices.map((office) => ({
                value: String(office.id),
                label: office.active
                  ? office.name
                  : `${office.name} (${t().customers.inactive})`,
              })),
            ]}
            onChange={(e) => setOfficeId(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      {loading || !report || !chartSeries ? (
        <LoadingSpinner message={t().common.loading} />
      ) : (
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain scrollbar-thin pb-2">
          <div className="grid gap-4 md:grid-cols-2">
            <SummaryBox
              title={labels.chargeTotal}
              invoice={report.totals.charges.invoice}
              cash={report.totals.charges.cash}
              total={report.totals.charges.total}
              invoiceLabel={labels.seriesInvoiceCharges}
              cashLabel={labels.seriesCashCharges}
            />
            <SummaryBox
              title={labels.paymentTotal}
              invoice={report.totals.payments.invoice}
              cash={report.totals.payments.cash}
              total={report.totals.payments.total}
              invoiceLabel={labels.paymentInvoice}
              cashLabel={labels.paymentCash}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card className="p-4">
              <h3 className="mb-3 text-sm font-semibold text-ink">
                {labels.chartChargesPayments}
              </h3>
              <GroupedBarChart
                months={months}
                series={chartSeries.grouped}
              />
            </Card>
            <Card className="p-4">
              <h3 className="mb-3 text-sm font-semibold text-ink">
                {labels.chartTotals}
              </h3>
              <LineChart months={months} series={chartSeries.totals} />
            </Card>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-ink">
              {labels.yearlyTable}
            </h3>
            <Card className="overflow-x-auto scrollbar-thin">
              <table className="w-full min-w-[44rem] text-sm">
                <thead className={dataTableHeadClass}>
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">
                      {labels.month}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      {labels.seriesInvoiceCharges}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      {labels.seriesCashCharges}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      {labels.chargeTotal}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      {labels.paymentInvoice}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      {labels.paymentCash}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      {labels.paymentTotal}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.months.map((row) => (
                    <tr key={row.month} className="border-t border-line">
                      <td className="whitespace-nowrap px-3 py-2 font-medium">
                        {months[row.month - 1]}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {formatMoney(row.charges.invoice)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {formatMoney(row.charges.cash)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium">
                        {formatMoney(row.charges.total)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {formatMoney(row.payments.invoice)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {formatMoney(row.payments.cash)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium">
                        {formatMoney(row.payments.total)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-line bg-surface-muted/40">
                    <td className="whitespace-nowrap px-3 py-2 font-semibold">
                      {labels.yearTotal}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-semibold">
                      {formatMoney(report.totals.charges.invoice)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-semibold">
                      {formatMoney(report.totals.charges.cash)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-semibold">
                      {formatMoney(report.totals.charges.total)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-semibold">
                      {formatMoney(report.totals.payments.invoice)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-semibold">
                      {formatMoney(report.totals.payments.cash)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-semibold">
                      {formatMoney(report.totals.payments.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </div>
        </div>
      )}
    </PageShell>
  );
}

function SummaryBox({
  title,
  invoice,
  cash,
  total,
  invoiceLabel,
  cashLabel,
}: {
  title: string;
  invoice: string;
  cash: string;
  total: string;
  invoiceLabel: string;
  cashLabel: string;
}) {
  return (
    <Card className="p-4 text-sm">
      <div className="font-semibold text-ink">{title}</div>
      <div className="mt-3 space-y-1">
        <div className="flex justify-between">
          <span>{invoiceLabel}</span>
          <span className="tabular-nums">{formatMoney(invoice)}</span>
        </div>
        <div className="flex justify-between">
          <span>{cashLabel}</span>
          <span className="tabular-nums">{formatMoney(cash)}</span>
        </div>
        <div className="flex justify-between font-semibold">
          <span>{t().financial.grandTotal}</span>
          <span className="tabular-nums">{formatMoney(total)}</span>
        </div>
      </div>
    </Card>
  );
}

type ChartSeries = {
  key: string;
  label: string;
  color: string;
  values: number[];
};

function ChartLegend({ series }: { series: ChartSeries[] }) {
  return (
    <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
      {series.map((s) => (
        <span key={s.key} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: s.color }}
          />
          {s.label}
        </span>
      ))}
    </div>
  );
}

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function formatAxis(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}εκ.`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}χιλ.`;
  return String(Math.round(value));
}

function GroupedBarChart({
  months,
  series,
}: {
  months: readonly string[];
  series: ChartSeries[];
}) {
  const width = 640;
  const height = 260;
  const pad = { top: 12, right: 12, bottom: 36, left: 52 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const maxValue = niceMax(Math.max(...series.flatMap((s) => s.values), 0));
  const groupCount = 12;
  const groupW = plotW / groupCount;
  const barGap = 2;
  const barW = Math.max(4, (groupW - 8 - barGap * (series.length - 1)) / series.length);
  const shortMonths = months.map((m) => m.slice(0, 3));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * maxValue);

  return (
    <div>
      <ChartLegend series={series} />
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full"
        role="img"
        aria-label={t().reports.chartChargesPayments}
      >
        {ticks.map((tick) => {
          const y = pad.top + plotH - (tick / maxValue) * plotH;
          return (
            <g key={tick}>
              <line
                x1={pad.left}
                x2={pad.left + plotW}
                y1={y}
                y2={y}
                stroke="rgb(var(--bf-line))"
                strokeWidth={1}
              />
              <text
                x={pad.left - 8}
                y={y + 3}
                textAnchor="end"
                fill="rgb(var(--bf-ink-muted))"
                fontSize={10}
              >
                {formatAxis(tick)}
              </text>
            </g>
          );
        })}
        {shortMonths.map((label, monthIndex) => {
          const groupX = pad.left + monthIndex * groupW + 4;
          return (
            <g key={label + monthIndex}>
              {series.map((s, seriesIndex) => {
                const value = s.values[monthIndex] ?? 0;
                const barH = (value / maxValue) * plotH;
                const x = groupX + seriesIndex * (barW + barGap);
                const y = pad.top + plotH - barH;
                return (
                  <rect
                    key={s.key}
                    x={x}
                    y={y}
                    width={barW}
                    height={Math.max(barH, value > 0 ? 1 : 0)}
                    fill={s.color}
                    rx={1}
                  >
                    <title>
                      {months[monthIndex]} — {s.label}: {formatMoney(value)}
                    </title>
                  </rect>
                );
              })}
              <text
                x={groupX + (series.length * (barW + barGap) - barGap) / 2}
                y={height - 12}
                textAnchor="middle"
                fill="rgb(var(--bf-ink-muted))"
                fontSize={10}
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function LineChart({
  months,
  series,
}: {
  months: readonly string[];
  series: ChartSeries[];
}) {
  const width = 640;
  const height = 260;
  const pad = { top: 12, right: 12, bottom: 36, left: 52 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const maxValue = niceMax(Math.max(...series.flatMap((s) => s.values), 0));
  const shortMonths = months.map((m) => m.slice(0, 3));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * maxValue);

  function point(monthIndex: number, value: number) {
    const x = pad.left + (monthIndex / 11) * plotW;
    const y = pad.top + plotH - (value / maxValue) * plotH;
    return { x, y };
  }

  return (
    <div>
      <ChartLegend series={series} />
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full"
        role="img"
        aria-label={t().reports.chartTotals}
      >
        {ticks.map((tick) => {
          const y = pad.top + plotH - (tick / maxValue) * plotH;
          return (
            <g key={tick}>
              <line
                x1={pad.left}
                x2={pad.left + plotW}
                y1={y}
                y2={y}
                stroke="rgb(var(--bf-line))"
                strokeWidth={1}
              />
              <text
                x={pad.left - 8}
                y={y + 3}
                textAnchor="end"
                fill="rgb(var(--bf-ink-muted))"
                fontSize={10}
              >
                {formatAxis(tick)}
              </text>
            </g>
          );
        })}
        {series.map((s) => {
          const d = s.values
            .map((value, i) => {
              const { x, y } = point(i, value);
              return `${i === 0 ? 'M' : 'L'}${x},${y}`;
            })
            .join(' ');
          return (
            <g key={s.key}>
              <path
                d={d}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {s.values.map((value, i) => {
                const { x, y } = point(i, value);
                return (
                  <circle key={i} cx={x} cy={y} r={3} fill={s.color}>
                    <title>
                      {months[i]} — {s.label}: {formatMoney(value)}
                    </title>
                  </circle>
                );
              })}
            </g>
          );
        })}
        {shortMonths.map((label, i) => {
          const { x } = point(i, 0);
          return (
            <text
              key={label + i}
              x={x}
              y={height - 12}
              textAnchor="middle"
              fill="rgb(var(--bf-ink-muted))"
              fontSize={10}
            >
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
